import { useCallback } from "react";
import { useOrgContext } from "../../../context/OrgContext";
import { useLocale } from "../../../context/LanguageContext";
import {
  useChatIntegration,
  useAvailableBroadcastChannels,
  useInitiateChatIntegration,
  useSelectBroadcastChannel,
  useUpdateBroadcastSettings,
  useDisconnectChatIntegration,
  useRefreshChatIntegrationStatus,
  useTestBroadcast,
} from "../hooks/useIntegrations";
import { SlackIntegrationCard } from "./SlackIntegrationCard";
import { PROVIDER_CONFIG, PROVIDER_DESCRIPTION_KEYS } from "../constants";
import msTeamsIcon from "../../../../assets/icons/ms-icon-panel.png";
import googleChatIcon from "../../../../assets/icons/gc-icon-panel.png";
import type {
  ChatProviderType,
  SelectBroadcastChannelInput,
  UpdateBroadcastSettingsInput,
} from "../types";

// ─── Coming Soon Placeholder ─────────────────────────────────────────────────

const PROVIDER_ICONS: Record<string, string> = {
  MS_TEAMS: msTeamsIcon,
  GOOGLE_CHAT: googleChatIcon,
};

function ComingSoonCard({ provider }: { provider: ChatProviderType }) {
  const config = PROVIDER_CONFIG[provider];
  const icon = PROVIDER_ICONS[provider];
  const { t } = useLocale();

  return (
    <div className="rounded-lg border border-border bg-card opacity-60">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            {icon ? (
              <img src={icon} alt={config.label} className="h-6 w-6" />
            ) : (
              <span className="text-lg">{config.emoji}</span>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {config.label}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t(PROVIDER_DESCRIPTION_KEYS[provider])}
            </p>
          </div>
        </div>
        <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
          {t("integrations.comingSoon")}
        </span>
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function IntegrationsSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-lg border border-border bg-card p-4"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-lg bg-muted" />
            <div className="space-y-1.5">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="h-3 w-48 animate-pulse rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function IntegrationsSection() {
  const { currentOrgId } = useOrgContext();
  const { t } = useLocale();
  const orgId = currentOrgId ?? "";

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data, loading, refetch } = useChatIntegration(orgId);
  const integration = data?.chatIntegration ?? null;

  const shouldFetchChannels =
    integration?.status === "CONNECTED" && !!integration;
  const {
    data: channelsData,
    loading: channelsLoading,
    refetch: refetchChannels,
  } = useAvailableBroadcastChannels(orgId, !shouldFetchChannels);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const [initiate] = useInitiateChatIntegration();
  const [selectChannel] = useSelectBroadcastChannel();
  const [updateSettings] = useUpdateBroadcastSettings();
  const [disconnect] = useDisconnectChatIntegration();
  const [refreshStatus] = useRefreshChatIntegrationStatus();
  const [testBroadcast] = useTestBroadcast();

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleConnect = useCallback(async () => {
    const result = await initiate({
      variables: { organizationId: orgId, provider: "SLACK" },
    });
    const authUrl = result.data?.initiateChatIntegration;
    if (!authUrl) throw new Error("No auth URL returned");
    return authUrl;
  }, [initiate, orgId]);

  const handleDisconnect = useCallback(async () => {
    await disconnect({ variables: { organizationId: orgId } });
  }, [disconnect, orgId]);

  const handleSelectChannel = useCallback(
    async (input: SelectBroadcastChannelInput) => {
      await selectChannel({
        variables: { organizationId: orgId, input },
      });
    },
    [selectChannel, orgId],
  );

  const handleUpdateSettings = useCallback(
    async (input: UpdateBroadcastSettingsInput) => {
      await updateSettings({
        variables: { organizationId: orgId, input },
      });
    },
    [updateSettings, orgId],
  );

  const handleRefreshStatus = useCallback(async () => {
    await refreshStatus({ variables: { organizationId: orgId } });
    await refetch();
  }, [refreshStatus, orgId, refetch]);

  const handleTestBroadcast = useCallback(async () => {
    await testBroadcast({ variables: { organizationId: orgId } });
  }, [testBroadcast, orgId]);

  const handleRefreshChannels = useCallback(() => {
    void refetchChannels();
  }, [refetchChannels]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!currentOrgId) return null;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-foreground">{t("integrations.title")}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("integrations.description")}
        </p>
      </div>

      {loading && !integration ? (
        <IntegrationsSkeleton />
      ) : (
        <div className="space-y-3">
          <SlackIntegrationCard
            integration={integration}
            channels={
              channelsData?.availableBroadcastChannels ?? []
            }
            channelsLoading={channelsLoading}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onSelectChannel={handleSelectChannel}
            onUpdateSettings={handleUpdateSettings}
            onRefreshStatus={handleRefreshStatus}
            onTestBroadcast={handleTestBroadcast}
            onRefreshChannels={handleRefreshChannels}
            onPopupClosed={() => { void refetch(); }}
          />
          <ComingSoonCard provider="MS_TEAMS" />
          <ComingSoonCard provider="GOOGLE_CHAT" />
        </div>
      )}
    </div>
  );
}
